// External libraries
// @ts-expect-error - no plans to type react-components for now
import { genericValidationFn, isString } from '@checkout/react-components';
import { getPasswordMaxLength } from '@/components/SignupFields/PasswordInput/passwordConstants';
import { validatePassword } from '@/components/SignupFields/PasswordInput/validations';

// Internal modules
import {
  getGlobals,
  getLandingPageUserEmail,
  getLocale,
  getUnifiedLoginContextData,
} from '@/lib/globals';
import { features } from '../../lib/features';
import { VALID_FIELD_STATE } from './constants';
import { getNationalityBasedKycValue } from '@/lib/features/utils';
import type { GenericObjectType } from '@/types';
import type {
  AddressValidationMeta,
  AddressValidationPayload,
  BaseAddressFieldResults,
  BaseAddressFields,
  CrsFieldResults,
  CrsFields,
  DefaultInputState,
} from './types';
import type { FormState } from './form/types';
import { CRS_TAXPAYERID_OPTIONS } from '@/components/CRSFields/constants';

// * Heavy use of GenericObjectType since much of this isn't well documented
// TODO: Replace most / all GenericObjectType with research into proper types

type DefaultStateKeys =
  | 'countryOfResidence'
  | 'identityDocumentType'
  | 'nationality'
  | 'placeOfBirth'
  | 'secondaryIdentityDocumentType';

const getDefaultKycStateOverride = (
  field: DefaultStateKeys,
): GenericObjectType => {
  const { country } = getLocale();

  const kycIdTypes = features.config.kycIdTypes;
  const defaultKycIdTypes = getNationalityBasedKycValue(kycIdTypes, country);

  const defaultValues = {
    countryOfResidence: country,
    identityDocumentType: defaultKycIdTypes[0],
    nationality: country,
    placeOfBirth: country,
    secondaryIdentityDocumentType: features.config.secondaryKycIdTypes[0],
  };
  const value = defaultValues[field];

  if (!value) {
    return {};
  }

  return {
    ...VALID_FIELD_STATE,
    value,
  };
};

const revalidatePassword = (state: FormState): GenericObjectType => {
  const props = {
    countryCode: getLocale().country,
    maxLength: getPasswordMaxLength(),
    user: {
      dateOfBirth: state.dateOfBirth?.value,
      firstName: state.firstName?.value,
      lastName: state.lastName?.value,
    },
  };

  return validatePassword(state.password.value, { props });
};

const wasInfoSecFieldChanged = (fieldName: string): boolean => {
  return /^(firstName|lastName|dateOfBirth)$/.test(fieldName);
};

const getDefaultInputState = <T extends GenericObjectType>(
  overrides = {} as T,
): DefaultInputState & T => ({
  displayError: false,
  error: '',
  errorCode: 'valueMissing',
  isDirty: false, // isDirty -> input that has had value change
  isValid: false,
  value: '',
  ...overrides,
});

/**
 * Returns the default email state for the store. If
 * the user entered a valid email on UL then it will use that email
 * as the value.
 */
const getDefaultEmailState = (): DefaultInputState => {
  const { email: ulEmail } = getUnifiedLoginContextData();
  const cartEmail =
    getGlobals().initialGqlData.checkoutSession?.cart?.email?.stringValue;
  const { value: landingPageUserEmail = '' } = getLandingPageUserEmail();

  const emailValue = landingPageUserEmail || ulEmail?.stringValue || cartEmail;

  if (!isString(emailValue)) {
    return getDefaultInputState();
  }

  const { isValid } = genericValidationFn(emailValue, {
    props: { required: true, type: 'email' },
  });

  const overrides = isValid
    ? { errorCode: '', isValid, value: emailValue }
    : {};

  return getDefaultInputState(overrides);
};

/**
 * @function getBaseAddressFields
 * @param {string} addressType
 * @returns {object}
 * @description Returns an object with shipping/billing state for
 * city, line1, line2, postcode, state
 */
const getBaseAddressFields = (): BaseAddressFieldResults => {
  const fields: BaseAddressFields[] = [
    'city',
    'houseNumber',
    'line1',
    'line2',
    'postalCode',
    'state',
    'streetName',
  ];
  return fields.reduce((accum, field) => {
    accum[field] = getDefaultInputState();
    return accum;
  }, {} as BaseAddressFieldResults);
};

const getAddressValidationPayload = <T extends string>({
  action,
  errorCode = '',
  fields,
}: {
  action: string;
  errorCode?: string;
  fields: T[];
}): AddressValidationPayload<T> & AddressValidationMeta => {
  const isSetError = action === 'setError';

  const fieldsUpdated = fields.reduce((accum, field) => {
    accum[field] = {
      displayError: isSetError,
      errorCode,
      isValid: !isSetError,
    };
    return accum;
  }, {} as AddressValidationPayload<T>);

  return { ...fieldsUpdated, meta: { hasValidationError: isSetError } };
};

/**
 * Helper function to get initial state for CRS tax info
 */
const getCrsFields = (): CrsFieldResults => {
  const fields: CrsFields[] = [
    'countryCode',
    'taxPayerIdType',
    'taxPayerIdValue',
    'noTaxPayerIdReason',
  ];
  return fields.reduce((accum, field) => {
    // Default tax payer id type to TIN
    if (field === 'taxPayerIdType') {
      accum[field] = CRS_TAXPAYERID_OPTIONS.TIN;
    } else {
      accum[field] = {
        displayError: false,
        error: '',
        value: '',
      };
    }
    return accum;
  }, {} as CrsFieldResults);
};

export {
  getAddressValidationPayload,
  getBaseAddressFields,
  getCrsFields,
  getDefaultEmailState,
  getDefaultInputState,
  getDefaultKycStateOverride,
  revalidatePassword,
  wasInfoSecFieldChanged,
};
